import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

interface Dimension {
  id: string;
  value: string;
}

export function DimensionConfig() {
  const [dimensions, setDimensions] = useState<Dimension[]>([
    { id: "1", value: "6000 mm" },
    { id: "2", value: "1200x2400 mm" },
    { id: "3", value: "3000 mm" },
  ]);
  const [newDimension, setNewDimension] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const { toast } = useToast();

  const handleAdd = () => {
    if (!newDimension.trim()) {
      toast({
        title: "Erro",
        description: "O valor da dimensão não pode estar vazio.",
        variant: "destructive",
      });
      return;
    }

    if (dimensions.some(dimension => dimension.value.toLowerCase() === newDimension.toLowerCase())) {
      toast({
        title: "Erro",
        description: "Já existe uma dimensão com este valor.",
        variant: "destructive",
      });
      return;
    }

    setDimensions([...dimensions, { id: Date.now().toString(), value: newDimension }]);
    setNewDimension("");
    toast({
      title: "Sucesso",
      description: "Dimensão adicionada com sucesso.",
    });
  };

  const handleEdit = (id: string, newValue: string) => {
    if (!newValue.trim()) {
      toast({
        title: "Erro",
        description: "O valor da dimensão não pode estar vazio.",
        variant: "destructive",
      });
      return;
    }

    if (dimensions.some(dimension => dimension.value.toLowerCase() === newValue.toLowerCase() && dimension.id !== id)) {
      toast({
        title: "Erro",
        description: "Já existe uma dimensão com este valor.",
        variant: "destructive",
      });
      return;
    }

    setDimensions(dimensions.map(dimension => 
      dimension.id === id ? { ...dimension, value: newValue } : dimension
    ));
    setEditingId(null);
    toast({
      title: "Sucesso",
      description: "Dimensão atualizada com sucesso.",
    });
  };

  const handleDelete = (id: string) => {
    setDimensions(dimensions.filter(dimension => dimension.id !== id));
    toast({
      title: "Sucesso",
      description: "Dimensão removida com sucesso.",
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex gap-4">
        <Input
          placeholder="Nova dimensão (ex: 6000 mm, 1200x2400 mm)"
          value={newDimension}
          onChange={(e) => setNewDimension(e.target.value)}
          className="max-w-sm bg-[#222831] text-white border-[#404040]"
        />
        <Button onClick={handleAdd} className="bg-[#0073B7] hover:bg-[#004080] text-white">
          <Plus className="h-4 w-4 mr-2" />
          Adicionar
        </Button>
      </div>

      <div className="bg-[#1A1F2C] rounded-md border border-[#404040]">
        <div className="divide-y divide-[#404040]">
          {dimensions.map((dimension) => (
            <div key={dimension.id} className="p-4 flex items-center justify-between text-white hover:bg-[#222831]">
              {editingId === dimension.id ? (
                <Input
                  defaultValue={dimension.value}
                  onBlur={(e) => handleEdit(dimension.id, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleEdit(dimension.id, e.currentTarget.value);
                    }
                  }}
                  className="max-w-sm bg-[#222831] text-white border-[#404040]"
                  autoFocus
                />
              ) : (
                <span>{dimension.value}</span>
              )}
              
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setEditingId(dimension.id)}
                  className="hover:bg-[#222831]"
                >
                  <Pencil className="h-4 w-4 text-[#0073B7]" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(dimension.id)}
                  className="hover:bg-[#222831]"
                >
                  <Trash2 className="h-4 w-4 text-[#E60026]" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}